import type { GlobeMethods } from 'react-globe.gl';
import { globeTextureUrls } from './constants';
import { getSunPosition, sunDirectionVector } from './globeDayNightMaterial';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ThreeNs = any;

let threeModule: ThreeNs | null = null;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let cloudsMesh: any = null;
let cloudsAnimationId = 0;
let cloudsSpinPaused = false;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let sunLight: any = null;

/** Pause cloud spin while user hovers a city (globe autoRotate is paused separately). */
export function setCloudsSpinPaused(paused: boolean) {
  cloudsSpinPaused = paused;
}

async function loadThree(): Promise<ThreeNs> {
  if (!threeModule) {
    threeModule = await import('three');
  }
  return threeModule;
}

function disposeClouds(scene: ThreeNs, THREE: ThreeNs) {
  if (cloudsAnimationId) {
    cancelAnimationFrame(cloudsAnimationId);
    cloudsAnimationId = 0;
  }
  if (!cloudsMesh) return;
  scene.remove(cloudsMesh);
  cloudsMesh.geometry.dispose();
  const material = cloudsMesh.material;
  if (Array.isArray(material)) {
    material.forEach((m: ThreeNs) => m.dispose());
  } else {
    material.dispose();
  }
  cloudsMesh = null;
}

export function syncSunLighting(globe: GlobeMethods, date = new Date()) {
  if (!sunLight) return;
  const [sunLng, sunLat] = getSunPosition(date);
  const dir = sunDirectionVector(sunLng, sunLat, 4.5);
  sunLight.position.set(dir.x, dir.y, dir.z);
}

/** Lights + rotating cloud shell; tuned for day/night shader (brighter dayside). */
export async function setupRealisticGlobeScene(globe: GlobeMethods): Promise<() => void> {
  const THREE = await loadThree();
  const scene = globe.scene();

  sunLight = new THREE.DirectionalLight(0xfff8ef, 1.35);
  syncSunLighting(globe);

  globe.lights([
    new THREE.AmbientLight(0x8fa3c4, 0.58),
    sunLight,
    new THREE.DirectionalLight(0x3d5a80, 0.22),
  ]);

  const lights = globe.lights();
  lights[2].position.set(-2.2, -0.6, -1.8);

  const radius = globe.getGlobeRadius();
  const loader = new THREE.TextureLoader();

  loader.load(globeTextureUrls.clouds, (cloudsTexture: ThreeNs) => {
    cloudsTexture.anisotropy = Math.min(8, globe.renderer().capabilities.getMaxAnisotropy());

    const cloudsGeometry = new THREE.SphereGeometry(radius * 1.014, 72, 72);
    const cloudsMaterial = new THREE.MeshPhongMaterial({
      map: cloudsTexture,
      transparent: true,
      opacity: 0.2,
      depthWrite: false,
    });

    disposeClouds(scene, THREE);
    cloudsMesh = new THREE.Mesh(cloudsGeometry, cloudsMaterial);
    scene.add(cloudsMesh);

    const spinClouds = () => {
      if (cloudsMesh && !cloudsSpinPaused) {
        cloudsMesh.rotation.y += 0.00038;
      }
      cloudsAnimationId = requestAnimationFrame(spinClouds);
    };
    spinClouds();
  });

  return () => {
    disposeClouds(scene, THREE);
    sunLight = null;
  };
}
